// Claude: Buyer/Subscription.tsx — buyer-facing subscription plan usage view 11-Apr-2026
// Claude: updated to show rfxOpenCount, buyerRFXLimits, creditsValue; removed meeting cards 20-Apr-2026
// Claude: pricePerRFX → subscriptionFee; creditBalance from profile; Tab 2 statement records 25-Apr-2026
import {
  Box,
  Button,
  Flex,
  Image,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Images } from "../../Assets/images";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { AdminBuyerAPI } from "../../Apis/adminBuyerAPI";
import Loader from "../../Components/Loader/Loader";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { MdArrowBack } from "react-icons/md";
import moment from "moment-timezone";

const BuyerSubscription = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user);
  const { success, error } = useToastWrapper();

  const [buyerData, setBuyerData] = useState<any>(null);
  const [statements, setStatements] = useState<any[]>([]);
  const [statementsLoading, setStatementsLoading] = useState(false);

  // Claude: fetch buyer profile — plan fields merged by backend 11-Apr-2026
  const getProfile = async () => {
    try {
      const res = await BuyerAPI.getBuyerProfile();
      if (res) setBuyerData(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  // Claude: fetch statement records for Tab 2 25-Apr-2026
  const getStatements = async () => {
    setStatementsLoading(true);
    try {
      const res = await BuyerAPI.getStatements();
      // Claude: guard against non-array response — ?? only guards null/undefined, not unexpected objects 27-04-2026
      setStatements(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.log(err);
    } finally {
      setStatementsLoading(false);
    }
  };

  // Claude: sends renewal request email to admin 11-Apr-2026
  const sendRenewRequest = async () => {
    try {
      await AdminBuyerAPI.requestBuyerSubscriptionPlan({
        buyerName: buyerData?.buyerPersonName,
        companyName: buyerData?.buyerCompanyName,
        planName: buyerData?.planName,
      });
      success("Your renewal request has been sent. Our team will be in touch shortly.");
    } catch (err) {
      error("Failed to send renewal request. Please contact support@aftr.network.");
    }
  };

  useEffect(() => { getProfile(); getStatements(); }, []);

  const currency = buyerData?.currency ?? "";

  const renderStatementRow = (record: any, i: number) => {
    if (record.recordType === "statement") {
      return (
        <Box key={i} bg="white" borderRadius="10px" px={4} py={3} mb={2} border="1px solid #D1FAE5">
          <Flex justifyContent="space-between" mb={1}>
            <Text fontSize="12px" fontWeight={600} color="#166534">Payment</Text>
            <Text fontSize="11px" color="gray.500">{record.date ? moment(record.date).format("DD/MM/YYYY") : "—"}</Text>
          </Flex>
          <Text fontSize="11px" color="gray.600" mb={1}>{record.planName ?? "—"}</Text>
          <Flex gap={4} flexWrap="wrap">
            <Text fontSize="11px" color="gray.500">Plan: {record.currency ?? currency} {record.planAmount ?? 0}</Text>
            <Text fontSize="11px" color="gray.500">Credits Used: {record.creditsUtilized ?? 0}</Text>
            <Text fontSize="11px" color="gray.500">Paid: {record.currency ?? currency} {record.paidAmount ?? 0}</Text>
            {record.rfxAdded > 0 && <Text fontSize="11px" color="gray.500">RFPs Added: {record.rfxAdded}</Text>}
          </Flex>
          {record.paymentReference && (
            <Text fontSize="10px" color="gray.400" mt={1}>Ref: {record.paymentReference}</Text>
          )}
        </Box>
      );
    }
    if (record.recordType === "credit") {
      return (
        <Box key={i} bg="white" borderRadius="10px" px={4} py={3} mb={2} border="1px solid #DBEAFE">
          <Flex justifyContent="space-between" mb={1}>
            <Text fontSize="12px" fontWeight={600} color="#1D4ED8">MAR Credit</Text>
            <Text fontSize="11px" color="gray.500">{record.date ? moment(record.date).format("DD/MM/YYYY") : "—"}</Text>
          </Flex>
          <Text fontSize="11px" color="gray.600" mb={1}>{record.rfxSnippet ?? "—"}</Text>
          <Flex gap={4} flexWrap="wrap">
            <Text fontSize="11px" color="gray.500">Amount: {record.currency ?? currency} {record.creditAmount ?? 0}</Text>
            {record.expiryDate && (
              <Text fontSize="11px" color="gray.500">Expires: {moment(record.expiryDate).format("DD/MM/YYYY")}</Text>
            )}
          </Flex>
        </Box>
      );
    }
    if (record.recordType === "creditBalance") {
      return (
        <Box key={i} bg="white" borderRadius="10px" px={4} py={3} mb={2} border="1px solid #E9E4FF">
          <Flex justifyContent="space-between" mb={1}>
            <Text fontSize="12px" fontWeight={600} color="#7C66FF">{record.notes ?? "Balance Update"}</Text>
            <Text fontSize="11px" color="gray.500">{record.date ? moment(record.date).format("DD/MM/YYYY") : "—"}</Text>
          </Flex>
          <Flex gap={4} flexWrap="wrap">
            <Text fontSize="11px" color="gray.500">Amount: {record.currency ?? currency} {record.creditAmount ?? 0}</Text>
            <Text fontSize="11px" color="gray.500">Opening: {record.openingBalance ?? 0}</Text>
            <Text fontSize="11px" fontWeight={600} color="#166534">Closing: {record.closingBalance ?? 0}</Text>
          </Flex>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box display="flex" flexDir="column" w="100%" minH="calc(100dvh + 85px)" pb="85px" bg="#F3F0FF">
      {/* Dark header */}
      <Flex w="100%" px="30px" py={5} bg="#0E122F" color="white" alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap={3} cursor="pointer" onClick={() => navigate(-1)}>
          <MdArrowBack size={20} color="white" />
          <Text fontSize="xl" fontWeight={700}>Subscription</Text>
        </Flex>
        <VStack spacing={0} alignItems="center" justifyContent="center">
          <Image src={Images.AftrLogo} h="32px" objectFit="contain" />
          <Text fontSize="11px" fontWeight={300} color="whiteAlpha.600" letterSpacing="wide">
            buyer (beta)
          </Text>
        </VStack>
      </Flex>

      {/* Content */}
      {!buyerData ? (
        <Loader />
      ) : !buyerData.planName ? (
        <Box px={5} pt={6}>
          <Text fontSize="sm" color="gray.600">
            You do not have any active subscription. Contact{" "}
            <Link href="mailto:support@aftr.com" color="blue.500">support@aftr.com</Link>
          </Text>
        </Box>
      ) : (
        <Tabs colorScheme="purple" mx={4} mt={4}>
          <TabList>
            <Tab fontSize="sm" fontWeight={600}>Plan</Tab>
            <Tab fontSize="sm" fontWeight={600}>Statement</Tab>
          </TabList>
          <TabPanels>
            {/* Tab 1 — Plan details */}
            <TabPanel px={0} pt={4}>
              <Box>
                {/* Plan name + status card */}
                <Box bg="white" borderRadius="14px" px={4} py={3} mb={3} shadow="sm" border="1px solid #E9E4FF">
                  <Flex justifyContent="space-between" alignItems="center">
                    <Text fontSize="sm" fontWeight={700} color="#0E122F">{buyerData.planName}</Text>
                    <Box px={3} py={1} borderRadius="full"
                      bg={(buyerData.rfxOpenCount ?? 0) >= (buyerData.buyerRFXLimits ?? 0) && (buyerData.buyerRFXLimits ?? 0) > 0 ? "#FEF2F2" : "#F0FDF4"}
                      border={`1px solid ${(buyerData.rfxOpenCount ?? 0) >= (buyerData.buyerRFXLimits ?? 0) && (buyerData.buyerRFXLimits ?? 0) > 0 ? "#FCA5A5" : "#86EFAC"}`}
                    >
                      <Text fontSize="11px" fontWeight={600}
                        color={(buyerData.rfxOpenCount ?? 0) >= (buyerData.buyerRFXLimits ?? 0) && (buyerData.buyerRFXLimits ?? 0) > 0 ? "#991B1B" : "#166534"}>
                        {(buyerData.rfxOpenCount ?? 0) >= (buyerData.buyerRFXLimits ?? 0) && (buyerData.buyerRFXLimits ?? 0) > 0 ? "Limit Reached" : "Active"}
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                {/* Metric rows */}
                <VStack spacing={2} align="stretch">
                  <Box bg="white" borderRadius="14px" px={4} py={3} shadow="sm" border="1px solid #E9E4FF">
                    <Flex justifyContent="space-between" alignItems="center" mb={1}>
                      <Text fontSize="13px" fontWeight={600} color="gray.700">Open RFPs</Text>
                      <Text fontSize="13px" fontWeight={700} color="#7C66FF">
                        {buyerData.rfxOpenCount ?? 0}{" "}
                        <Text as="span" fontWeight={400} color="gray.400">/ {buyerData.buyerRFXLimits ?? "—"}</Text>
                      </Text>
                    </Flex>
                    <Text fontSize="11px" color="gray.400">Currently open RFPs against your allowed limit.</Text>
                  </Box>

                  {/* Subscription Fee card */}
                  {(buyerData.subscriptionFee ?? 0) > 0 && (
                    <Box bg="white" borderRadius="14px" px={4} py={3} shadow="sm" border="1px solid #E9E4FF">
                      <Flex justifyContent="space-between" alignItems="center" mb={1}>
                        <Text fontSize="13px" fontWeight={600} color="gray.700">Subscription Fee</Text>
                        <Text fontSize="13px" fontWeight={700} color="#7C66FF">
                          {currency} {buyerData.subscriptionFee}
                        </Text>
                      </Flex>
                    </Box>
                  )}

                  {/* RFP Response Pending Limit card */}
                  {buyerData.rfpResponsePendingLimit > 0 && (
                    <Box bg="white" borderRadius="14px" px={4} py={3} shadow="sm" border="1px solid #E9E4FF">
                      <Flex justifyContent="space-between" alignItems="center" mb={1}>
                        <Text fontSize="13px" fontWeight={600} color="gray.700">Response Pending Limit</Text>
                        <Text fontSize="13px" fontWeight={700} color="#7C66FF">{buyerData.rfpResponsePendingLimit}</Text>
                      </Flex>
                      <Text fontSize="11px" color="gray.400">Max unreviewed responses shown at a time.</Text>
                    </Box>
                  )}

                  {/* Credit Balance card */}
                  {(buyerData.creditBalance ?? 0) > 0 && (
                    <Box bg="white" borderRadius="14px" px={4} py={3} shadow="sm" border="1px solid #E9E4FF">
                      <Flex justifyContent="space-between" alignItems="center" mb={1}>
                        <Text fontSize="13px" fontWeight={600} color="gray.700">Credit Balance</Text>
                        <Text fontSize="13px" fontWeight={700} color="#166534">
                          {currency} {(buyerData.creditBalance ?? 0).toFixed(2)}
                        </Text>
                      </Flex>
                      <Text fontSize="11px" color="gray.400">Credit toward your next payment.</Text>
                    </Box>
                  )}
                </VStack>

                <Flex mt={5} justifyContent="space-between" alignItems="center">
                  <Text fontSize="10px" color="gray.400" fontStyle="italic">
                    View <Link href="https://prontocommerce.com/aftr/subscriptions/" isExternal textDecoration="underline">T&Cs</Link>
                  </Text>
                  <Button size="sm" bg="#F3F0FF" color="#7C66FF" borderRadius="10px" px={5}
                    _hover={{ bg: "#E9E4FF" }} onClick={sendRenewRequest}
                  >
                    Renew / TopUp
                  </Button>
                </Flex>
              </Box>
            </TabPanel>

            {/* Tab 2 — Statement records */}
            <TabPanel px={0} pt={4}>
              {statementsLoading ? (
                <Loader />
              ) : statements.length === 0 ? (
                <Text fontSize="sm" color="gray.500">No statement records found.</Text>
              ) : (
                <Box>
                  {statements.map((record: any, i: number) => renderStatementRow(record, i))}
                </Box>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
};

export default BuyerSubscription;
